import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "@/lib/observability";
import {
  looksLikeStaleChunk,
  manualPurgeAndReload,
  tryStaleChunkRecovery,
} from "@/lib/stale-chunk-guard";

interface Props { children: ReactNode }
interface State { error: Error | null; componentStack: string | null; recovering: boolean }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null, recovering: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
    // Surface the component stack in state so the boundary UI can
    // render it — without it we're staring at a minified frame chain
    // with no idea which component is undefined.
    this.setState({ componentStack: info.componentStack ?? null });
    // Delegates to the unified guard so the sessionStorage / cache-bust
    // loop protection applies here too. Returns false if we already
    // tried recovery this session — in that case fall through to UI.
    if (!this.state.recovering) {
      const triggered = tryStaleChunkRecovery(error);
      if (triggered) this.setState({ recovering: true });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const stale = looksLikeStaleChunk(this.state.error);

    return (
      <div style={{ padding: 32, maxWidth: 640, margin: "60px auto", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>
          {stale && this.state.recovering ? "Refreshing the app…" : "Something went wrong"}
        </h1>
        <p className="muted" style={{ fontSize: 14, marginBottom: 16 }}>
          {stale && this.state.recovering
            ? "A new version was deployed since you opened this tab. Clearing the cache and reloading. If this page doesn't reload automatically, click below."
            : stale
              ? "We already tried refreshing automatically and the page still crashed. Click below to clear all cached data and try once more."
              : "The page crashed. The error has been reported. You can try reloading."}
        </p>
        <pre style={{ background: "var(--warm-canvas, #f5f3ec)", padding: 12, borderRadius: 8, fontSize: 12, overflow: "auto", maxHeight: 220 }}>
          {this.state.error.message}
          {this.state.componentStack && (
            <>
              {"\n\nComponent stack:"}
              {this.state.componentStack}
            </>
          )}
          {this.state.error.stack && (
            <>
              {"\n\nJS stack:\n"}
              {this.state.error.stack}
            </>
          )}
        </pre>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={() => manualPurgeAndReload()}
                  style={{ padding: "8px 16px", border: 0, background: "#16a34a", color: "#fff", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>
            Clear cache &amp; reload
          </button>
          <button onClick={() => window.location.reload()}
                  style={{ padding: "8px 16px", border: "1px solid var(--warm-border, #d4d4d4)", background: "transparent", color: "var(--warm-text, #1a1a1a)", borderRadius: 8, cursor: "pointer" }}>
            Simple reload
          </button>
          {stale && (
            <a href={`${window.location.pathname}?_chunkguard=off`}
               style={{ padding: "8px 16px", border: "1px solid var(--warm-border, #d4d4d4)", background: "transparent", color: "var(--warm-text, #1a1a1a)", borderRadius: 8, cursor: "pointer", textDecoration: "none", fontSize: 13 }}>
              Disable auto-recovery
            </a>
          )}
        </div>
      </div>
    );
  }
}
